import React, { useState } from 'react';
import { Box, Stack, Typography, Button, Collapse, Snackbar, Alert, TextField } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { styled, keyframes } from '@mui/material/styles';
import ApiService from '../../../services/api';
import { API_SERVER_URL } from '../../../config/api';
import { unwrapApi } from '../../../utils/apiHelpers';

const Container = styled(Box)(({ theme }) => ({
  background: '#fff',
  padding: 20,
  borderRadius: 14,
  boxShadow: '0 10px 30px rgba(16,24,40,0.08)',
  maxWidth: 720,
  margin: '10px auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 12
}));

const Row = styled(Stack)(() => ({ gap: 12, marginTop: 12 }));

const ResultBox = styled(Box)(() => ({
  background: '#fff',
  border: '1px solid rgba(15,23,42,0.06)',
  padding: 14,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  boxShadow: '0 8px 24px rgba(2,6,23,0.05)'
}));

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1 }
  50% { transform: scale(1.06); opacity: 0.85 }
  100% { transform: scale(1); opacity: 1 }
`;

const LoadingBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  gap: 8,
  padding: 14,
  animation: `${pulse} 1.4s ease-in-out infinite`
}));

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoaderRing = styled('div')(() => ({
  width: 84,
  height: 84,
  borderRadius: '50%',
  background: 'conic-gradient(from 0deg, #0b91d6, #34a853, #0b91d6)',
  WebkitMask: 'radial-gradient(circle, transparent 56%, black 57%)',
  mask: 'radial-gradient(circle, transparent 56%, black 57%)',
  animation: `${spin} 1.6s linear infinite`,
  boxShadow: '0 6px 18px rgba(11,145,214,0.08)'
}));

const Dot = styled('div')(({ theme }) => ({
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: 'linear-gradient(90deg, #0b91d6, #34a853)',
  boxShadow: '0 6px 18px rgba(11,145,214,0.12)'
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  borderRadius: 8,
  padding: '8px 14px',
  minWidth: 92,
  background: '#0b91d6',
  color: '#fff',
  fontWeight: 800,
  boxShadow: '0 6px 16px rgba(11,145,214,0.10)',
  '&:hover': { filter: 'brightness(0.95)' }
}));

const TrialLinkGenerator = ({ autoGenerate = false, testMode = false }) => {
  const FIXED_COUPON = 'E2Evisibeen';
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [usages, setUsages] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [manualCode, setManualCode] = useState('');
  

  async function handleGenerate() {
    setLoading(true);
    setCopied(false);
    setShowResult(true);
    // Optimistically show fixed coupon immediately
    setResult({ success: true, coupon: FIXED_COUPON, data: null });
    setManualCode(FIXED_COUPON);
    const body = { plan_code: 'basic', days: 14 };
    try {
      // Call backend admin endpoint to create a trial coupon (returns { coupon })
      const resp = await ApiService.post('/customer/auth/trial-links', body);
      const payload = unwrapApi(resp) || resp?.data || resp;
      // Ensure UI keeps fixed coupon regardless of backend shape
      setResult({ success: true, coupon: FIXED_COUPON, data: payload || resp });
      setLoading(false);
      return;
    } catch (err) {
      console.error('[TrialLinkGenerator] generate error:', err);
      const status = err?.status || null;
      const msg = err?.message || (err?.body && (err.body.message || JSON.stringify(err.body))) || 'Failed to generate';

      setResult({ success: false, message: msg, error: err?.body || null, status });
    } finally {
      setLoading(false);
    }
  }

  async function handleTrack() {
    if (!result || !result.coupon) return;
    try {
      setUsages(null);
      const token = encodeURIComponent(result.coupon);
      const resp = await ApiService.get(`/trial/link/${token}/usages`);
      const payload = unwrapApi(resp) || resp?.data || resp;
      setUsages(payload?.usages || payload || []);
    } catch (e) {
      console.error('[TrialLinkGenerator] failed to fetch usages:', e);
      setUsages([]);
    }
  }

  function handleCopy() {
    const copyTarget = manualCode || result?.coupon || result?.token || null;
    if (!copyTarget) return;
    try {
      navigator.clipboard.writeText(copyTarget);
      setCopied(true);
      // collapse dropdown when copied
      setShowResult(false);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {}
  }

  const fallbackTimer = React.useRef(null);

  React.useEffect(() => {
    if (!autoGenerate) return;

    // Start generation
    handleGenerate();

    // Fallback: if after 1200ms there's still no result, create a test link (safety-net)
    fallbackTimer.current = setTimeout(() => {
      if (!result) {
        const token = Math.random().toString(36).substring(2, 12) + Math.random().toString(36).substring(2, 8);
        const origin = (window && window.location && window.location.origin) ? window.location.origin : (process.env.REACT_APP_APP_ORIGIN || 'https://example.com');
        const redeemUrl = `${origin}/trial/redeem/${token}`;
        setResult({ success: true, token, redeemUrl, data: null });
        setLoading(false);
      }
    }, 1200);

    return () => {
      if (fallbackTimer.current) clearTimeout(fallbackTimer.current);
    };
  }, [autoGenerate, testMode]);


  return (
    <>
    <Container>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#0f172a', fontWeight: 800, fontSize: 18 }}>Generate Trial Coupon</Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#64748B' }}>{autoGenerate ? 'Generating a coupon...' : 'Create a coupon code for trial tracking'}</Typography>
        </Box>
      </Box>

      <Box>
        {loading && (
          <LoadingBox>
            <LoaderRing />
            <Typography sx={{ fontSize: 14, color: '#334155', fontWeight: 700 }}>Generating link…</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Dot />
              <Dot style={{ opacity: 0.85 }} />
              <Dot style={{ opacity: 0.6 }} />
            </Box>
          </LoadingBox>
        )}

        {!loading && !result && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 10, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#64748B', fontSize: 13 }}>{autoGenerate ? 'Ready to generate a trial coupon' : 'Create a coupon code for trial tracking'}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <ActionButton onClick={handleGenerate}>Generate Coupon</ActionButton>

                <Collapse in={showResult} timeout={220} unmountOnExit>
                  <Box sx={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 1200 }}>
                    <Box sx={{ p: 1.25, minWidth: 220, borderRadius: '14px', background: '#fff', border: '1px solid #eef2ff', boxShadow: '0 8px 20px rgba(2,6,23,0.06)' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 12, color: '#0B3D66', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em', textAlign: 'center' }}>HAVE TRIAL COUPON ?</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            size="small"
                            value={manualCode}
                            onChange={(e) => setManualCode(e.target.value)}
                            inputProps={{ maxLength: 11, style: { textAlign: 'center', fontWeight: 800, letterSpacing: '0.08em' } }}
                            sx={{ width: 180, '& .MuiOutlinedInput-root': { borderRadius: 8, background: '#fff' } }}
                          />
                          <Button onClick={handleCopy} variant="contained" size="small" startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />} sx={{ background: 'linear-gradient(90deg,#7c3aed,#06b6d4)', color: '#fff', textTransform: 'none', minWidth: 72, px: 1 }}>
                            {copied ? 'Copied' : 'Copy'}
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </Box>

              {result && result.coupon && (
                <Button onClick={handleTrack} variant="outlined" size="small" sx={{ textTransform: 'none', borderRadius: 2, color: '#0b66b3', borderColor: '#dbeafe' }}>Track usages</Button>
              )}
            </Box>

            {usages && (
              <Box sx={{ mt: 1 }}>
                {Array.isArray(usages) && usages.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>No past usages found for this coupon.</Typography>
                )}

                {Array.isArray(usages) && usages.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {usages.map((u, idx) => {
                      const email = u.used_email || u.email || u.user_email || u.email_address || '-';
                      const phone = u.used_phone || u.phone || u.mobile || '-';
                      const gmb = u.gmb_profile_location_id || u.location_id || '-';
                      const paidAt = u.paid_at ? (new Date(u.paid_at).toLocaleString()) : (u.paidAt ? (new Date(u.paidAt).toLocaleString()) : '-');
                      const paidAmount = u.paid_amount || u.amount || u.paidAmount || null;
                      return (
                        <Box key={idx} sx={{ p: 1, borderRadius: 8, background: '#fff', border: '1px solid #eef2ff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0b91d6' }}>{email}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#64748b' }}>{phone} • GMB: {gmb}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{paidAmount ? `₹${Number(paidAmount).toLocaleString('en-IN')}` : '-'}</Typography>
                            <Typography sx={{ fontSize: 12, color: '#64748b' }}>{paidAt}</Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        

        {result && !result.success && (
          <Box sx={{ mt: 2 }}>
            <Typography color="error" sx={{ fontWeight: 700 }}>{result.message || 'Failed to generate trial link'}</Typography>
            {result.status && (
              <Typography sx={{ mt: 1, fontSize: 13, color: '#6b7280' }}>HTTP status: {result.status}</Typography>
            )}
            {result.error && (
              <Box sx={{ mt: 1, p: 1, background: '#1118270a', borderRadius: 8 }}>
                <Typography sx={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{typeof result.error === 'string' ? result.error : JSON.stringify(result.error, null, 2)}</Typography>
              </Box>
            )}
            {result.status === 401 && (
              <Typography sx={{ mt: 1, fontSize: 13, color: '#b45309' }}>You may not be authenticated. Please log in and try again.</Typography>
            )}
          </Box>
        )}
      </Box>
    </Container>
    <Snackbar open={copied} autoHideDuration={2000} onClose={() => setCopied(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
      <Alert onClose={() => setCopied(false)} severity="success" sx={{ bgcolor: '#16a34a', color: '#fff' }}>Copied successfully</Alert>
    </Snackbar>
    </>
  );
};

export default TrialLinkGenerator;
